import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  
  AlertCircle,
  ChevronRight,
  AlertTriangle,
  FlaskConical,
  MessageSquareHeart,
  Trash2
} from "lucide-react";
import { LateSubmissionsManager } from "@/components/admin/LateSubmissionsManager";
import { FeedbackSurveyModal } from "@/components/dashboard/FeedbackSurveyModal";
import { DeleteAccountSurveyModal } from "@/components/admin/DeleteAccountSurveyModal";

interface DashboardStats {
  pendingUsers: number;
  pendingPackages: number;
  awaitingConfirmation: number;
  pickupsToday: number;
  confirmedPickups: number;
  pickedUp: number;
  delivered: number;
  lateSubmissions: number;
}

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    pendingUsers: 0,
    pendingPackages: 0,
    awaitingConfirmation: 0,
    pickupsToday: 0,
    confirmedPickups: 0,
    pickedUp: 0,
    delivered: 0,
    lateSubmissions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showFeedbackSurvey, setShowFeedbackSurvey] = useState(false);
  const [showDeleteSurvey, setShowDeleteSurvey] = useState(false);

  const fetchStats = async () => {
    try {
      // Fetch pending users
      const { count: pendingUsers } = await (supabase as any)
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("approval_status", "pending");

      // Fetch package stats
      const { data: packages } = await (supabase as any)
        .from("packages")
        .select("approval_status, status, requires_driver_approval, driver_approved");

      const pendingPackages = packages?.filter((p: any) => p.approval_status === "pending").length || 0;
      const awaitingConfirmation = packages?.filter((p: any) => p.approval_status === "approved" && p.status === "pending").length || 0;
      const pickedUp = packages?.filter((p: any) => p.status === "picked_up").length || 0;
      const delivered = packages?.filter((p: any) => p.status === "delivered").length || 0;
      const lateSubmissions = packages?.filter((p: any) => 
        p.requires_driver_approval === true && p.driver_approved === null
      ).length || 0;

      // Use optimized pickup stats function
      const { data: pickupStats } = await (supabase as any).rpc("get_pickup_stats");

      setStats({
        pendingUsers: pendingUsers || 0,
        pendingPackages,
        awaitingConfirmation,
        pickupsToday: pickupStats?.today_pickups || 0,
        confirmedPickups: pickupStats?.confirmed_pickups || 0,
        pickedUp,
        delivered,
        lateSubmissions,
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching dashboard stats:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();

    // Subscribe to realtime updates on user_pickups
    const channel = supabase
      .channel("admin-dashboard-pickups")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_pickups",
        },
        () => {
          // Refetch stats when pickup status changes
          fetchStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const statCards = [
    {
      title: "Pending User Approvals",
      value: stats.pendingUsers,
      icon: Users,
      color: "text-amber-500",
      bgColor: "bg-amber-50",
      link: "/admin/users?filter=pending",
      disabled: false,
    },
    {
      title: "Pending Package Review",
      value: stats.pendingPackages,
      icon: AlertCircle,
      color: "text-orange-500",
      bgColor: "bg-orange-50",
      link: "/admin/packages?status=pending_review",
      disabled: false,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your logistics operations</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {statCards.map((card) => (
          <Card 
            key={card.title} 
            className={`shadow-sm transition-all ${card.disabled ? 'opacity-50 cursor-default' : 'cursor-pointer hover:shadow-md hover:border-primary/50 group'}`}
            onClick={() => !card.disabled && navigate(card.link)}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                {card.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
            </CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-3xl font-bold">
                {loading ? "..." : card.value}
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Late Submissions Section */}
      <LateSubmissionsManager />

      {/* Dev Tools Section */}
      <Card className="shadow-sm border-dashed border-2 border-muted-foreground/20">
        <CardHeader className="flex flex-row items-center gap-2">
          <div className="p-2 rounded-lg bg-violet-50">
            <FlaskConical className="h-5 w-5 text-violet-500" />
          </div>
          <div>
            <CardTitle className="text-lg">Dev Tools</CardTitle>
            <p className="text-muted-foreground text-sm">Test UI components before they appear to users</p>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowFeedbackSurvey(true)}
            className="gap-2"
          >
            <MessageSquareHeart className="h-4 w-4" />
            Test Feedback Survey
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowDeleteSurvey(true)}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Test Deletion Survey
          </Button>
        </CardContent>
      </Card>

      {/* Quick Actions Section */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Click any stat card above to navigate directly to that section, or use the sidebar for full navigation.
          </p>
        </CardContent>
      </Card>

      {/* Test Modals */}
      <FeedbackSurveyModal
        open={showFeedbackSurvey}
        onOpenChange={setShowFeedbackSurvey}
        onTakeSurvey={() => setShowFeedbackSurvey(false)}
        onMaybeLater={() => setShowFeedbackSurvey(false)}
      />
      <DeleteAccountSurveyModal
        open={showDeleteSurvey}
        onOpenChange={setShowDeleteSurvey}
      />
    </div>
  );
};
